import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { MarketMapBase } from "@/components/maps/MarketMapBase";
import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";
import { useQuery } from "@/utils/query";
import { clearLocations, getPickupLocation } from "@/state/searchSlice";
import { MarketPin } from "@/components/common/svgs";
import { HoverPopup } from "./HoverPopup";
import { MapViewToggle } from "./MapViewToggle";
import { ZoomControls } from "./ZoomControls";
import { usePickupMarker, useMarketToggles, useRadiusCircle } from "@/components/maps/hooks";
import { MarketLoadsList } from "../MarketLoadsList";

export const PredictionMarketsMap = ({
    setHasError = () => {},
    outbounds,
    optimizePer,
    filterOutbounds,
    pickupMarkets = [],
    excludedPickupMarkets = [],
    onTogglePickupMarket = () => {},
    isLocked = false,
    marketLoads = {},
    themes = [],
    }) => {
    const dispatch = useDispatch();
    const { pickupZip, pickupRadius } = useQuery();
    const { pickupLocation } = useSelector((state) => state.search);
    const isDarkTheme = useIsDarkTheme();
    const isLoadboard = themes.includes("loadboard");
    const hideControls = themes.includes("hideControls");

    const [mapViewMode, setMapViewMode] = useState(isLoadboard ? "market" : "fullMap");
    const [shouldSetPins, setShouldSetPins] = useState(false);
    const [isMapReady, setIsMapReady] = useState(false);

    const marketMapRef = useRef(null);

    // Callback when map is initialised
    const handleMapInitialised = useCallback(() => {
        setIsMapReady(true);
    }, []);

    // Fetch pickup location when zip changes
    useEffect(() => {
        if (!pickupZip) return;
        dispatch(getPickupLocation({ zip: pickupZip }));
    }, [pickupZip, dispatch]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            dispatch(clearLocations());
        };
    }, [dispatch]);

    // Handle outbounds changes - trigger pin update
    useEffect(() => {
        if (outbounds && Object.keys(outbounds).length > 0) {
            setShouldSetPins(true);
        }
    }, [outbounds, optimizePer]);

    // Maintain selected market layer (border around pickup markets)
    useEffect(() => {
        if (!isMapReady || !marketMapRef.current || !pickupLocation.data) return;

        const mapInstance = marketMapRef.current;
        const map = mapInstance.getMap();
        if (!map) return;

        const hasRadiusSearch = pickupRadius && pickupRadius !== "market-area";

        // Only show market borders in market view mode, or in fullMap mode without radius search
        if (mapViewMode === "market" || !hasRadiusSearch) {
            const selectedMarkets = pickupMarkets.length > 0
                ? pickupMarkets
                : [pickupLocation.data.market.city];
            mapInstance.addSelectedMarketLayer(selectedMarkets);
        } else {
            mapInstance.removeSelectedMarketLayer();
        }

        // Ensure overlay layers stay on top
        mapInstance.moveOverlayLayersToTop();
    }, [isMapReady, pickupLocation.data, pickupMarkets, excludedPickupMarkets, mapViewMode, pickupRadius]);

    // Add market pins
    useEffect(() => {
        if (!isMapReady || !marketMapRef.current) return;

        const mapInstance = marketMapRef.current;
        const map = mapInstance.getMap();
        const marketCenters = mapInstance.getMarketCenters();

        if (
            !map ||
            marketCenters?.loading ||
            !marketCenters?.data ||
            !filterOutbounds ||
            !pickupZip ||
            !pickupLocation.data
        ) {
            return;
        }

        if (!shouldSetPins) {
            return;
        }

        // Remove all existing pins
        mapInstance.removeAllPins();

        // Add market pins
        filterOutbounds.forEach((market, index) => {
            const marketCenter = marketCenters.data[market.zip];
            if (marketCenter) {
                const point = [marketCenter.longitude, marketCenter.latitude];
                mapInstance.addPin(
                    map,
                    () => <MarketPin number={index + 1} isDarkTheme={isDarkTheme} />,
                    point,
                    -13
                );
            }
        });

        // Ensure overlay layers stay on top after adding pins
        mapInstance.moveOverlayLayersToTop();

        setShouldSetPins(false);
    }, [isMapReady, shouldSetPins, pickupLocation.data, filterOutbounds, pickupZip, isDarkTheme]);

    // Pickup marker with zoom-based scaling
    usePickupMarker({
        isMapReady,
        mapRef: marketMapRef,
        pickupLocation,
        mapViewMode,
    });

    // Market toggle checkboxes and click handling
    useMarketToggles({
        isMapReady,
        mapRef: marketMapRef,
        pickupLocation,
        pickupMarkets,
        excludedPickupMarkets,
        mapViewMode,
        onTogglePickupMarket,
    });

    // Radius circle overlay and view mode transitions
    useRadiusCircle({
        isMapReady,
        mapRef: marketMapRef,
        pickupLocation,
        pickupRadius,
        mapViewMode,
        shouldSetPins,
        isLoadboard,
    });

    const mainPickupMarket = pickupLocation.data?.market?.city;

    const marketLoadsItems = useMemo(() => {
        const o = {};
        if (mainPickupMarket != null) o[mainPickupMarket] = marketLoads[mainPickupMarket] ?? 0;
        pickupMarkets.forEach((m) => {
            o[m] = marketLoads[m] ?? 0;
        });
        return o;
    }, [pickupMarkets, mainPickupMarket, marketLoads]);

    const marketLoadsValue = useMemo(() => {
        const v = {};
        if (mainPickupMarket != null) v[mainPickupMarket] = true;
        pickupMarkets.forEach((m) => {
            if (!excludedPickupMarkets.includes(m)) v[m] = true;
        });
        return v;
    }, [pickupMarkets, excludedPickupMarkets, mainPickupMarket]);

    const handleMarketLoadsSetValue = useCallback(
      (newValue) => {
          const allMarketKeys = new Set([...Object.keys(newValue), ...Object.keys(marketLoadsValue)]);
          allMarketKeys.forEach((m) => {
              if (m === mainPickupMarket) return;
              if (newValue[m] !== marketLoadsValue[m]) onTogglePickupMarket(m);
          });
      },
      [marketLoadsValue, onTogglePickupMarket, mainPickupMarket]
    );

    return (
        <MarketMapBase
            ref={marketMapRef}
            outbounds={outbounds}
            optimizePer={optimizePer}
            onError={() => setHasError(true)}
            onInitialised={handleMapInitialised}
            HoverPopupComponent={HoverPopup}
            isLocked={isLocked}
        >
            <>
                {mapViewMode === "market" && mainPickupMarket != null && (
                  <div className={`absolute ${isLoadboard ? "left-4" : "left-2"} top-1/2 -translate-y-1/2 z-10`}>
                      <MarketLoadsList
                        items={marketLoadsItems}
                        value={marketLoadsValue}
                        setValue={handleMarketLoadsSetValue}
                        centralMarketKey={mainPickupMarket}
                      />
                  </div>
                )}
                {!hideControls && (<div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-10">
                    <ZoomControls mapRef={marketMapRef} />
                    <MapViewToggle
                      mapRef={marketMapRef}
                      mapViewMode={mapViewMode}
                      setMapViewMode={setMapViewMode}
                    />
                </div>)}
            </>
        </MarketMapBase>
    );
};
